import { makeStyles, styled } from "@mui/styles";
import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Button,
  Card,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  TableHead,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Refresh } from "@mui/icons-material";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import TablePaginationActions from "../../../table/TablePaginationActions";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import moment from "moment";
import { getRefreshTime } from "../../../../utils/getRefreshTime";
import { useNavigate } from "react-router-dom";
import {
  customerActions,
  deleteCustomer,
  getCustomer,
  sortCustomer,
  updateCustomer,
} from "../../../../redux/reducer/customer/customerReducer";
import { getCustomerRegion } from "../../../../redux/reducer/customerRegion/customerRegionReducer";
import { getCustomerGroup } from "../../../../redux/reducer/customerGroup/customerGroupReducer";
import TableDropwdownActions from "../../../table/TableDropwdownActions";

const useStyles = makeStyles(() => ({
  headerClassSearch: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    padding: "0px 5px",
    margin: "10px 0px",
    background: "#F2F2F2",
  },
  tableHead: {
    backgroundColor: "#f5f2e6",
  },
  actionButtonDelete: {
    backgroundColor: "#dde0f0",
    color: "#A0A3B3",
    fontSize: "25px",
    borderRadius: "5px",
    marginRight: "10px",
    cursor: "pointer",
    "&:hover": {
      background: "#ba2941",
      color: "white",
    },
  },
  actionButtonEdit: {
    backgroundColor: "#dde0f0",
    color: "#A0A3B3",
    fontSize: "25px",
    borderRadius: "5px",
    marginRight: "10px",
    cursor: "pointer",
    "&:hover": {
      background: "#7dbda9",
      color: "white",
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#499178",
    color: theme.palette.common.white,
    border: "none!important",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "#f0faf6",
    cursor: "pointer",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#f0faf6",
    },
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function AdminCustomer() {
  // Navigate
  const navigate = useNavigate();

  // Styling
  const styles = useStyles();

  //Redux
  const data = useSelector((state) => state.customer.data);
  const token = useSelector((state) => state.auth.token);
  const getTime = useSelector((state) => state.customer.updatedTime);
  const region = useSelector((state) => state.customerRegion.data);
  const group = useSelector((state) => state.customerGroup.data);
  const dispatch = useDispatch();

  // Local State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formInput, setFormInput] = useState({
    to: "",
    from: "",
    limit: 0,
  });
  const [sortingField, setSortingField] = useState([]);
  const [searchField, setSearchField] = useState([]);
  const [fetch, setFetch] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [timeUpdated, setTimeUpdated] = useState({
    hour: 0,
    minute: 0,
  });

  useEffect(() => {
    if (fetch) {
      handleAddSort();
      handleAddSearch();
      setFetch(false);
    }
    if (getTime > 0) {
      setTimeUpdated(getRefreshTime(getTime));
    }
  }, [fetch, getTime]);

  // Handling Sorting
  const handleSort = (index) => (e) => {
    const newItems = [...sortingField];
    newItems[index][e.target.name] = e.target.value;
    setSortingField(newItems);
  };

  const handleAddSort = () => {
    setSortingField((prevItems) => [
      ...prevItems,
      {
        sorting: "asc",
        field: "createdAt",
      },
    ]);
  };

  const handleDeleteSort = (value) => {
    let deletedData = [];
    for (let i = 0; i < sortingField.length; i++) {
      if (i !== value) {
        deletedData.push(sortingField[i]);
      }
    }
    setSortingField(deletedData);
  };

  // Handle Search
  const handleChangeSearch = (index) => (e) => {
    const newItems = [...searchField];
    newItems[index][e.target.name] = e.target.value;
    setSearchField(newItems);
  };

  const handleAddSearch = () => {
    setSearchField((prevItems) => [
      ...prevItems,
      {
        value: "",
        field: "",
      },
    ]);
  };

  const handleDeleteSearch = (value) => {
    let deletedData = [];
    for (let i = 0; i < searchField.length; i++) {
      if (i !== value) {
        deletedData.push(searchField[i]);
      }
    }
    setSearchField(deletedData);
  };

  const handleChangeInput = (e) => {
    setFormInput({ ...formInput, [e.target.name]: e.target.value });
  };

  const handleChangePage = (event, newPage) => {
    if (event) {
      setPage(newPage);
      const getData = {
        params: {
          page: newPage + 1,
          limit: 10,
        },
        token: token,
      };
      dispatch(getCustomer(getData));
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const column = [
    "Kode",
    "Tipe",
    "Nama",
    "Region ",
    "Group",
    "Tenor Pembayaran",
    "PIC",
    "Aksi",
  ];

  const deleteItem = (id) => {
    Swal.fire({
      title: `Apakah anda yakin ingin menghapus permanent customer ini?`,
      icon: "warning",
      buttons: true,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Hapus",
      cancelButtonText: "Batal",
    }).then((confirm) => {
      if (confirm.isConfirmed) {
        const deleteData = {
          data: id,
          token: token,
        };
        dispatch(deleteCustomer(deleteData)).then((res) => {
          if (res.payload.status === 200) {
            dispatch(customerActions.deleteCustomer(id));
            toast.success(res.payload.message);
          } else {
            toast.error(res.payload.message);
          }
        });
      } else {
        return;
      }
    });
  };

  const deleteClassItem = (id) => {
    Swal.fire({
      title: `Apakah anda yakin ingin menghapus customer ini?`,
      icon: "warning",
      buttons: true,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Hapus",
      cancelButtonText: "Batal",
    }).then((confirm) => {
      if (confirm.isConfirmed) {
        const deleteData = {
          value: {
            isActive: false,
          },
          id: id,
          token: token,
        };
        dispatch(updateCustomer(deleteData)).then((res) => {
          if (res.payload.status === 200) {
            dispatch(customerActions.deleteCustomer(id));
            toast.success(res.payload.message);
          } else {
            toast.error(res.payload.message);
          }
        });
      } else {
        return;
      }
    });
  };

  const activeClassItem = (id) => {
    Swal.fire({
      title: `Apakah anda yakin ingin mengaktifkan customer ini?`,
      icon: "warning",
      buttons: true,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Aktifkan",
      cancelButtonText: "Batal",
    }).then((confirm) => {
      if (confirm.isConfirmed) {
        const activatedData = {
          value: {
            isActive: true,
          },
          id: id,
          token: token,
        };
        dispatch(updateCustomer(activatedData)).then((res) => {
          if (res.payload.status === 200) {
            dispatch(
              getCustomer({
                params: {
                  params: {},
                },
                token: token,
              })
            );
            toast.success(res.payload.message);
          } else {
            toast.error(res.payload.message);
          }
        });
      } else {
        return;
      }
    });
  };

  const checkingFilter = (pageValue) => {
    let sort = [];
    sortingField.map((item) => {
      if (item.sorting === "asc") {
        sort.push(`${item.field}`);
      } else {
        sort.push(`-${item.field}`);
      }
      return "";
    });

    let body = {};
    for (let i = 0; i < searchField.length; i++) {
      if (
        searchField[i].value !== "" &&
        (searchField[i].field === "customerName" ||
          searchField[i].field === "customerCode" || 
          searchField[i].field === "pic" || 
          searchField[i].field === "timeOfPayment" || 
          searchField[i].field === "typeOfCustomer"  
          )
      ) {
        body[searchField[i].field] = {
          $regex: searchField[i].value,
          $options: "i",
        };
      } else if (
        searchField[i].field === "customerGroupId" ||
        searchField[i].field === "customerRegionId"
      ) {
        body[searchField[i].field] = searchField[i].value;
      }
    }

    if (formInput.from) {
      if (formInput.to) {
        body = {
          ...body,
          createdAt: {
            $gte: moment(formInput.from).format(),
            $lte: moment(formInput.to).format(),
          },
        };
      } else {
        body = {
          ...body,
          createdAt: {
            $gte: moment(formInput.from).format(),
            $lte: moment(formInput.to)
              .add(23, "hours")
              .add(59, "minutes")
              .format(),
          },
        };
      }
    }

    let filterData = {
      token: token,
      body: {
        ...body,
        isActive: isActive,
      },
      params: {
        page: pageValue,
        limit: formInput.limit > 0 ? formInput.limit : 10,
        paginate: true,
        sort: sort.toString().replaceAll(",", " "),
      },
    };
    return filterData;
  };

  const handleSubmit = () => {
    dispatch(sortCustomer(checkingFilter(1))).then((res) => {
      toast.success(res.payload.message);
    });
  };

  const handleReset = () => {
    setSortingField([]);
    setIsActive(true);
    setSearchField([]);
    handleAddSearch();
    handleAddSort();
    setFormInput({
      to: "",
      from: "",
      limit: 0,
    });
  };

  const handleUpdate = () => {
    const getData = {
      params: {
        params: {},
      },
      token: token,
    };
    dispatch(getCustomer(getData));
  };

  const handleEditData = (id) => {
    navigate("/admin/customer/edit/" + id);
  };

  const loadMore = (value) => (event) => {
    if (
      event.target.scrollHeight - event.target.scrollTop < 300 &&
      event.target.scrollTop < 100
    ) {
      if (
        region.totalDocs !== region.docs.length &&
        value === 1
      ) {
        const getData = {
          params: {
            limit: region.limit + 10,
            page: 1,
          },
          token: token,
        };
        dispatch(getCustomerRegion(getData));
      } else if (
        group.totalDocs !== group.docs.length &&
        value === 2
      ) {
        const getData = {
          params: {
            limit: group.limit + 10,
            page: 1,
          },
          token: token,
        };
        dispatch(getCustomerGroup(getData));
      } }
  };
  return (
    <>
      <ToastContainer />
      <Card className={styles.headerClassSearch}>
        <Grid padding={0.5} container spacing={0.5} flexDirection={"column"}>
          <Grid
            item
            container
            xs={12}
            sm={12}
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <div
              style={{ display: "flex", alignItems: "center", width: "10%" }}
            >
              <Typography variant="h6" color="primary">
                PENCARIAN
              </Typography>
            </div>
            <div style={{ display: "flex", width: "50%" }}>
              <FormControl
                variant="standard"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: "row",
                  width: "80%",
                  margin: "10px",
                }}
              >
                <TextField
                  InputLabelProps={{ shrink: true }}
                  label="Dari"
                  name="from"
                  size="small"
                  type="date"
                  onChange={handleChangeInput}
                  value={formInput.from}
                  variant="outlined"
                  style={{ width: "49%" }}
                />
                <TextField
                  InputLabelProps={{ shrink: true }}
                  label="Ke"
                  name="to"
                  size="small"
                  type="date"
                  onChange={handleChangeInput}
                  value={formInput.to}
                  variant="outlined"
                  style={{ width: "49%" }}
                />
              </FormControl>
              <FormControl style={{ margin: "10px", width: "20%" }}>
                <TextField
                  onChange={handleChangeInput}
                  InputLabelProps={{ shrink: true }}
                  type="number"
                  size="small"
                  label="Limit"
                  name="limit"
                  value={formInput.limit}
                  fullWidth
                  variant="outlined"
                  style={{ width: "100%", maxWidth: "450px" }}
                />
              </FormControl>
            </div>
          </Grid>
          <Divider style={{ width: "100%", border: "1px solid black" }} />
          <Grid
            item
            container
            xs={12}
            sm={12}
            direction="column"
            justifyContent="space-evenly"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "80%",
              }}
            >
              <div style={{ width: "15%" }}>
                <Typography>Kata Kunci</Typography>
              </div>
              <div style={{ width: "85%" }}>
                {searchField.map((item, index) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                      key={index}
                    >
                      <FormControl
                        variant="standard"
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          flexDirection: "row",
                          width: "67.5%",
                          minWidth: "67.5%",
                          margin: "5px",
                        }}
                      >
                        <TextField
                          select
                          size="small"
                          name="field"
                          id={index}
                          onChange={handleChangeSearch(index)}
                          defaultValue="customerCode"
                          value={item.field}
                          variant="outlined"
                          style={{ marginRight: "10px", width: "30%" }}
                        >
                          <MenuItem value="customerCode">Kode</MenuItem>
                          <MenuItem value="typeOfCustomer">
                            Tipe Customer
                          </MenuItem>
                          <MenuItem value="customerRegionId">Region</MenuItem>
                          <MenuItem value="customerName">Nama</MenuItem>
                          <MenuItem value="timeOfPayment">
                            Tenor Pembayaran
                          </MenuItem>
                          <MenuItem value="customerGroupId">Group</MenuItem>
                          <MenuItem value="pic">PIC</MenuItem>
                        </TextField>

                        {item.field !== "customerCode" &&
                        item.field !== "typeOfCustomer" && item.field !== "customerName" &&
                        item.field !== "timeOfPayment" && item.field !== "pic"  ? (
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            select
                            required
                            size="small"
                            error={false}
                            name="value"
                            onChange={handleChangeSearch(index)}
                            value={item.value}
                            variant="outlined"
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  onScroll: loadMore(
                                    item.field === "customerRegionId"
                                      ? 1 : 2
                                  ),
                                },
                                style: {
                                  height: "300px",
                                },
                              },
                            }}
                            style={{
                              width: "65%",
                              maxWidth: "400px",
                              marginRight: "10px",
                            }}
                          >
                            {item.field === "customerGroupId" &&
                            group.docs.length > 0
                              ? group.docs.map((data, index) => (
                                  <MenuItem value={data._id} key={index}>
                                    {data.groupName}
                                  </MenuItem>
                                ))
                              : item.field === "customerGroupId" && (
                                  <MenuItem value="">Data kosong</MenuItem>
                                )}
                            {item.field === "customerRegionId" &&
                            region.docs.length > 0
                              ? region.docs.map((data, index) => (
                                  <MenuItem value={data._id} key={index}>
                                    {data.regionName}
                                  </MenuItem>
                                ))
                              : item.field === "customerRegionId" && (
                                  <MenuItem value="">Data kosong</MenuItem>
                                )}
                       
                          </TextField>
                        ) : (
                          <TextField
                            id={index}
                            type="text"
                            size="small"
                            name="value"
                            onChange={handleChangeSearch(index)}
                            defaultValue="asc"
                            value={item.value}
                            variant="outlined"
                            style={{ width: "65%" }}
                          />
                        )}
                      </FormControl>
                      {searchField.length < 7 ? (
                        <AddIcon
                          className={styles.actionButtonEdit}
                          onClick={() => handleAddSearch()}
                        />
                      ) : (
                        ""
                      )}
                      {searchField.length > 1 && index !== 0 ? (
                        <RemoveIcon
                          className={styles.actionButtonDelete}
                          onClick={() => handleDeleteSearch(index)}
                        />
                      ) : (
                        ""
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "80%",
              }}
            >
              <div style={{ width: "15%" }}>
                <Typography>Urutkan</Typography>
              </div>
              <div style={{ width: "85%" }}>
                {sortingField.map((item, index) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                      key={index}
                    >
                      <FormControl
                        variant="standard"
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          flexDirection: "row",
                          width: "67.5%",
                          minWidth: "67.5%",
                          margin: "5px",
                        }}
                      >
                        <TextField
                          select
                          size="small"
                          name="field"
                          id={index}
                          onChange={handleSort(index)}
                          defaultValue="customerCode"
                          value={item.field}
                          variant="outlined"
                          style={{ marginRight: "10px", width: "30%" }}
                        >
                          {" "}
                          <MenuItem value="customerCode">Kode</MenuItem>
                          <MenuItem value="typeOfCustomer">
                            Tipe Customer
                          </MenuItem>
                          <MenuItem value="customerRegionId">Region</MenuItem>
                          <MenuItem value="customerName">Nama</MenuItem>
                          <MenuItem value="timeOfPayment">
                            Tenor Pembayaran
                          </MenuItem>
                          <MenuItem value="customerGroupId">Group</MenuItem>
                          <MenuItem value="pic">PIC</MenuItem>
                          <MenuItem value="createdAt">Tanggal</MenuItem>
                        </TextField>

                        <TextField
                          id={index}
                          select
                          size="small"
                          name="sorting"
                          onChange={handleSort(index)}
                          defaultValue="asc"
                          value={item.sorting}
                          variant="outlined"
                          style={{ width: "65%" }}
                        >
                          <MenuItem value="asc">Teratas</MenuItem>
                          <MenuItem value="desc">Terbawah</MenuItem>
                        </TextField>
                      </FormControl>
                      {sortingField.length < 8 ? (
                        <AddIcon
                          className={styles.actionButtonEdit}
                          onClick={() => handleAddSort()}
                        />
                      ) : (
                        ""
                      )}

                      {sortingField.length > 1 && index !== 0 ? (
                        <RemoveIcon
                          className={styles.actionButtonDelete}
                          onClick={() => handleDeleteSort(index)}
                        />
                      ) : (
                        ""
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "80%",
              }}
            >
              <div style={{ width: "15%" }}>
                <Typography>Status Data</Typography>
              </div>
              <div style={{ width: "85%" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <FormControl
                    variant="standard"
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      flexDirection: "row",
                      width: "67.5%",
                      minWidth: "67.5%",
                      margin: "5px",
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      name="field"
                      onChange={(e) => setIsActive(e.target.value)}
                      defaultValue="true"
                      value={isActive}
                      variant="outlined"
                      style={{ marginRight: "10px", width: "30%" }}
                    >
                      <MenuItem value="true">Aktif</MenuItem>
                      <MenuItem value="false">Tidak Aktif</MenuItem>
                    </TextField>
                  </FormControl>
                </div>
              </div>
            </div>
          </Grid>
          <Grid
            container
            item
            justifyContent="flex-end"
            xs={12}
            sm={12}
            marginBottom={1}
          >
            <Button
              variant="contained"
              size="small"
              style={{ margin: "0px 5px" }}
              onClick={() => handleSubmit()}
            >
              Submit
            </Button>
            <Button
              variant="contained"
              size="small"
              color="error"
              style={{ margin: "0px 10px" }}
              onClick={() => handleReset()}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Card
        style={{
          padding: "10px",
          background: "#F2F2F2",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            margin: "10px 5px",
          }}
        >
          <div>
            <Button
              variant="contained"
              onClick={() => navigate("/admin/customer/tambah")}
            >
              Tambah Customer
            </Button>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Refresh
              className={styles.actionButtonEdit}
              onClick={() => handleUpdate()}
            />
            <i>
              updated {timeUpdated.hour > 0 ? `${timeUpdated.hour} hour` : ""}{" "}
              {`${timeUpdated.minute} minutes ago`}
            </i>
          </div>
        </div>
        <Paper
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "column",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead className={styles.classTableHead}>
                <StyledTableRow>
                  {column.length > 0 &&
                    column.map((item, index) => (
                      <StyledTableCell align="center" key={index}>
                        {item}
                      </StyledTableCell>
                    ))}
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {data.docs.length > 0 ? (
                  data.docs.map((row, index) => (
                    <StyledTableRow
                      key={index}>
                      <StyledTableCell style={{ width: 160 }} align="center">
                        {row.customerCode ? row.customerCode : "-"}
                      </StyledTableCell>
                      <StyledTableCell style={{ width: 160 }} align="center">
                        {row.typeOfCustomer ? row.typeOfCustomer : "-"}
                      </StyledTableCell>
                      <StyledTableCell style={{ width: 160 }} align="center">
                        {row.customerName ? row.customerName : "-"}
                      </StyledTableCell>
                      <StyledTableCell style={{ width: 160 }} align="center">
                        {row.customerRegionId
                          ? row.customerRegionId.regionName
                          : "-"}
                      </StyledTableCell>
                      <StyledTableCell style={{ width: 160 }} align="center">
                        {row.customerGroupId
                          ? row.customerGroupId.groupName
                          : "-"}
                      </StyledTableCell>
                      <StyledTableCell style={{ width: 160 }} align="center">
                        {row.timeOfPayment ? row.timeOfPayment : "-"}
                      </StyledTableCell>
                      <StyledTableCell style={{ width: 160 }} align="center">
                        {row.pic ? row.pic : "-"}
                      </StyledTableCell>
                      <StyledTableCell style={{ width: 170 }} align="center">
                      <TableDropwdownActions
                          handleEditData={() => handleEditData(row._id)}
                          handleDetailData={() => navigate(`/admin/customer/detail/${row._id}`)}
                          deleteClassItem={() => deleteClassItem(row._id)}
                          deleteItem={() => deleteItem(row._id)}
                          activeClassItem={() => activeClassItem(row._id)}
                          styles={styles}
                          row={row}
                        />
                        {/* <EditIcon
                          className={styles.actionButtonEdit}
                          fontSize="small"
                          onClick={(e) => handleEditData(e, row._id)}
                        />

                        <Delete
                          className={styles.actionButtonDelete}
                          fontSize="small"
                          onClick={(e) => deleteClassItem(e, row._id)}
                        />
                        <DeleteSweep
                          className={styles.actionButtonDelete}
                          fontSize="small"
                          onClick={(e) => deleteItem(e, row._id)}
                        />
                        {!row.isActive && (
                          <AddToPhotos
                            className={styles.actionButtonEdit}
                            fontSize="small"
                            onClick={(e) => activeClassItem(e, row._id)}
                          />
                        )} */}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableRow>
                    <StyledTableCell style={{ width: "100%" }} align="center">
                      Tidak ada data
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {data.docs.length > 0 ? (
            <TablePagination
              rowsPerPageOptions={""}
              className={styles.pagination}
              rowsPerPage={rowsPerPage}
              colSpan={12}
              count={data.totalDocs}
              page={page}
              SelectProps={{
                inputProps: {
                  "aria-label": "rows per page",
                },
                native: true,
              }}
              sx={{ width: "100%" }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          ) : (
            ""
          )}
        </Paper>
      </Card>
    </>
  );
}
